import {
  type ClipColorGrading,
  type CpuGradingStages,
  type LUTData,
} from "./color-grading-engine";
import {
  isNeutralCurves,
  isNeutralHsl,
  isNeutralLut,
} from "./color-grading-defaults";
import type { Effect } from "../types/timeline";

export type RenderableClipColorGrading = Omit<ClipColorGrading, "lut"> & {
  lut?: LUTData;
};

/**
 * Normalisers that turn a clip's stored grading into the shape the render
 * path wants. Pure.
 */

export function buildCpuGradingStages(
  colorGrading: RenderableClipColorGrading,
): CpuGradingStages | null {
  const stages: CpuGradingStages = {};
  let hasStage = false;

  if (colorGrading.curves && !isNeutralCurves(colorGrading.curves)) {
    stages.curves = colorGrading.curves;
    hasStage = true;
  }
  if (colorGrading.lut && !isNeutralLut(colorGrading.lut)) {
    stages.lut = colorGrading.lut;
    hasStage = true;
  }
  if (colorGrading.hsl && !isNeutralHsl(colorGrading.hsl)) {
    stages.hsl = colorGrading.hsl;
    hasStage = true;
  }

  return hasStage ? stages : null;
}

export function normalizeClipColorGrading(
  colorGrading: ClipColorGrading,
): RenderableClipColorGrading {
  const lut = normalizeClipLut(colorGrading.lut);
  return {
    ...colorGrading,
    lut,
  };
}

export function normalizeClipLut(
  lut: ClipColorGrading["lut"] | undefined,
): LUTData | undefined {
  if (!lut) {
    return undefined;
  }

  return {
    data:
      lut.data instanceof Uint8Array
        ? lut.data
        : new Uint8Array(lut.data),
    size: lut.size,
    intensity: lut.intensity,
  };
}

export function buildWhiteBalanceEffects(
  colorGrading: RenderableClipColorGrading,
): Effect[] {
  const effects: Effect[] = [];
  if (
    colorGrading.temperature !== undefined &&
    colorGrading.temperature !== 0
  ) {
    effects.push({
      id: "wb-temperature",
      type: "temperature",
      enabled: true,
      params: { value: colorGrading.temperature },
    });
  }
  if (colorGrading.tint !== undefined && colorGrading.tint !== 0) {
    effects.push({
      id: "wb-tint",
      type: "tint",
      enabled: true,
      params: { value: colorGrading.tint },
    });
  }
  return effects;
}
